/**
 * UnifiedWorkQueue - Consolidated Data Specialist Work Queue
 * ==========================================================
 * 
 * Combines three separate queues into one tabbed interface:
 * - Case assignments (from DataSpecialistQueue)
 * - Transaction reviews (from TransactionReviewQueue)
 * - Data issues (from IssueTrackerDashboard)
 * 
 * Feature Flag: UNIFIED_WORK_QUEUE
 * When disabled, users see the original separate pages.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../utils/api';
import { pluralize } from '../utils/pluralize';
import AppLayout from '../components/AppLayout';
import { Toaster, toast } from 'sonner';
import {
  Inbox,
  AlertTriangle,
  CheckCircle2,
  Clock,
  RefreshCw,
  ChevronRight,
  UserCheck,
  FileWarning,
  Search,
  Loader2,
  PanelRightOpen,
  PanelRightClose,
} from 'lucide-react';

// Phase 3 Dashboard Components
import TopPrioritiesCard from '../components/dashboard/TopPrioritiesCard';
import DataQualityWidget from '../components/dashboard/DataQualityWidget';
import AlertsPanel from '../components/dashboard/AlertsPanel';

// ==================== Types ====================

interface CaseItem {
  type: 'case';
  id: string;
  business_name: string;
  law_firm: string | null;
  event_type: string | null;
  assigned_queue: string;
  queue_reason: string;
  pipeline_stage: string;
  workflow_status: string | null;  // For 90% automation: "awaiting_client", "in_progress", etc.
  estimated_value: number;
  case_strength: number;
  transactions_need_review: number;
  completion_percentage: number;
  days_stale: number;
  last_updated: string | null;
  primary_action: string;
  primary_action_url: string;
  priority: 'critical' | 'high' | 'medium' | 'low';
}

interface TransactionItem {
  type: 'transaction' | 'awaiting_response';
  id: string;
  request_id?: string;
  report_id: string;
  claimant_name: string;
  law_firm_name?: string;
  transaction_id: string;
  question: string;
  client_response?: string;
  client_category?: string;
  client_notes?: string;
  ai_confidence?: number;
  received_at?: string;
  source?: string;
  transaction_date?: string;
  transaction_amount?: number;
  transaction_description?: string;
  current_category?: string;
  priority: 'critical' | 'high' | 'medium' | 'low';
  days_waiting?: number;
}

interface IssueItem {
  type: 'issue';
  id: string;
  report_id: string;
  title: string;
  description: string;
  issue_type: string;
  status: string;
  priority: 'critical' | 'high' | 'medium' | 'low';
  affected_amount?: number;
  what_would_fix?: string;
  affected_items?: string[];
  detected_by: string;
  created_at: string;
  days_open?: number;
}

type WorkItem = CaseItem | TransactionItem | IssueItem;

interface UnifiedStats {
  // From /api/v1/queue/stats
  total_cases: number;
  needs_attention: number;
  no_action_needed: number;
  data_specialist: number;
  client: number;
  accountant: number;
  ai_processing: number;
  ready: number;
  attorney: number;
  stale_5_days: number;
  stale_7_days: number;
  stale_14_days: number;
  total_value: number;
  cases_with_transactions_needing_review: number;
}

// ==================== Constants ====================

const TABS = [
  { id: 'needs_review', label: 'Needs Review', icon: UserCheck },
  { id: 'awaiting', label: 'Awaiting Client', icon: Clock },
  { id: 'issues', label: 'Data Issues', icon: FileWarning },
] as const;

type TabId = typeof TABS[number]['id'];

// ==================== Helper Functions ====================

const getPriorityScore = (priority: string): number => {
  switch (priority) {
    case 'critical': return 4;
    case 'high': return 3;
    case 'medium': return 2;
    case 'low': return 1;
    default: return 0;
  }
};

const sortByPriorityAndAge = (items: WorkItem[]): WorkItem[] => {
  return [...items].sort((a, b) => {
    // First by priority (higher first)
    const priorityDiff = getPriorityScore(b.priority) - getPriorityScore(a.priority);
    if (priorityDiff !== 0) return priorityDiff;
    
    // Then by age (older first)
    const aDays = 'days_stale' in a ? a.days_stale : ('days_waiting' in a ? a.days_waiting : ('days_open' in a ? a.days_open : 0)) || 0;
    const bDays = 'days_stale' in b ? b.days_stale : ('days_waiting' in b ? b.days_waiting : ('days_open' in b ? b.days_open : 0)) || 0;
    return bDays - aDays;
  });
};

// ==================== Main Component ====================

export default function UnifiedWorkQueue() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // State
  const [activeTab, setActiveTab] = useState<TabId>(
    (searchParams.get('tab') as TabId) || 'needs_review'
  );
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSidebar, setShowSidebar] = useState(true); // Phase 3: Sidebar state
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 50;
  
  // Data
  const [cases, setCases] = useState<CaseItem[]>([]);
  const [transactions, setTransactions] = useState<TransactionItem[]>([]);
  const [awaitingResponse, setAwaitingResponse] = useState<TransactionItem[]>([]);
  const [issues, setIssues] = useState<IssueItem[]>([]);
  const [stats, setStats] = useState<UnifiedStats | null>(null);
  
  // Processing
  const [processingId, setProcessingId] = useState<string | null>(null);

  // Fetch cases with pagination
  const fetchCases = useCallback(async (page: number) => {
    try {
      const caseData = await api.get(`/api/v1/queue/data-specialist?page=${page}&page_size=${pageSize}`, { showErrorToast: false }) || { cases: [], total_count: 0, total_pages: 1 };
      
      // Update pagination state
      setTotalCount(caseData.total_count || 0);
      setTotalPages(caseData.total_pages || 1);
      
      // Transform cases
      const caseItems: CaseItem[] = (caseData.cases || []).map((c: any) => {
        let priority: CaseItem['priority'] = 'medium';
        if (c.days_stale >= 7) {
          priority = 'critical';
        } else if (c.days_stale >= 5) {
          priority = 'high';
        } else if (c.transactions_need_review > 0) {
          priority = 'high';
        }

        return {
          type: 'case' as const,
          id: c.id || '',
          business_name: c.business_name || 'Unknown Case',
          law_firm: c.law_firm || null,
          event_type: c.event_type || null,
          assigned_queue: c.assigned_queue || 'data_specialist',
          queue_reason: c.queue_reason || '',
          pipeline_stage: c.pipeline_stage || 'intake',
          workflow_status: c.workflow_status || null,  // For 90% automation
          estimated_value: c.estimated_value || 0,
          case_strength: c.case_strength || 0,
          transactions_need_review: c.transactions_need_review || 0,
          completion_percentage: c.completion_percentage || 0,
          days_stale: c.days_stale || 0,
          last_updated: c.last_updated || null,
          primary_action: c.primary_action || 'View',
          primary_action_url: c.primary_action_url || `/wizard-v2?claim=${c.id}`,
          priority,
        };
      });
      
      setCases(caseItems);
    } catch (e) {
      console.warn('Failed to fetch cases:', e);
    }
  }, [pageSize]);

  // Fetch all data sources
  const fetchData = useCallback(async (page: number = 1) => {
    setLoading(true);
    try {
      // Fetch stats from the proper endpoint
      let statsData = null;
      try {
        statsData = await api.get('/api/v1/queue/stats?user_role=data_specialist', { showErrorToast: false });
      } catch (e) {
        console.warn('Failed to fetch stats:', e);
      }
      
      if (statsData) {
        setStats(statsData as UnifiedStats);
      }

      // Fetch cases with pagination
      await fetchCases(page);

      // For now, transactions and issues can be fetched separately if those endpoints exist
      // The clarifications endpoint - try to get client clarifications needing review
      let transactionData = { items: [], client_clarifications: [] };
      try {
        transactionData = await api.get('/api/v1/clarifications/review-queue?limit=50&view=flat', { showErrorToast: false }) || { items: [], client_clarifications: [] };
      } catch (e) {
        console.warn('Failed to fetch transactions:', e);
      }

      // Map items that need review (client responses, pending requests, etc.)
      const reviewItems: TransactionItem[] = (transactionData.items || []).map((t: any) => ({
        type: 'awaiting_response' as const,
        id: t.id || t.request_id || '',
        request_id: t.request_id,
        report_id: t.report_id || '',
        claimant_name: t.claimant_name || 'Unknown',
        law_firm_name: t.law_firm_name,
        transaction_id: t.transaction_id || '',
        question: t.question || t.client_response || '',
        client_response: t.client_response,
        client_category: t.client_category,
        client_notes: t.client_notes,
        ai_confidence: t.ai_confidence,
        received_at: t.received_at,
        source: t.source,
        transaction_date: t.transaction_date,
        transaction_amount: t.transaction_amount,
        transaction_description: t.transaction_description,
        current_category: t.current_category,
        priority: t.priority || 'medium',
        days_waiting: t.days_waiting || 0,
      }));
      
      // Also add any client_clarifications
      const clientItems: TransactionItem[] = (transactionData.client_clarifications || []).map((t: any) => ({
        type: 'transaction' as const,
        id: t.id || t.response_id || '',
        request_id: t.request_id,
        report_id: t.report_id || '',
        claimant_name: t.claimant_name || 'Unknown',
        law_firm_name: t.law_firm_name,
        transaction_id: t.transaction_id || '',
        question: t.question || '',
        client_response: t.client_response,
        client_category: t.client_category,
        client_notes: t.client_notes,
        ai_confidence: t.ai_confidence,
        received_at: t.received_at,
        source: t.source,
        transaction_date: t.transaction_date,
        transaction_amount: t.transaction_amount,
        transaction_description: t.transaction_description,
        current_category: t.current_category,
        priority: t.priority || 'medium',
        days_waiting: t.days_waiting || 0,
      }));
      
      setTransactions(clientItems);
      setAwaitingResponse(reviewItems);

      // Fetch pending issues from global endpoint
      let issuesData = { issues: [] };
      try {
        issuesData = await api.get('/api/v1/issues/pending?limit=50', { showErrorToast: false }) || { issues: [] };
      } catch (e) {
        console.warn('Failed to fetch issues:', e);
      }
      
      const issueItems: IssueItem[] = (issuesData.issues || []).map((i: any) => ({
        type: 'issue' as const,
        id: i.id || '',
        report_id: i.report_id || '',
        title: i.title || 'Unknown Issue',
        description: i.description || '',
        issue_type: i.issue_type || 'unknown',
        status: i.status || 'pending_review',
        priority: (i.priority || 'medium').toLowerCase() as IssueItem['priority'],
        affected_amount: i.affected_amount,
        what_would_fix: i.what_would_fix || '',
        affected_items: i.affected_items || [],
        detected_by: i.detected_by || 'system',
        created_at: i.created_at || '',
        days_open: i.created_at ? Math.floor((Date.now() - new Date(i.created_at).getTime()) / (1000 * 60 * 60 * 24)) : 0,
      }));
      setIssues(issueItems);

    } catch (err) {
      console.error('Failed to fetch queue data:', err);
      toast.error('Failed to load work queue');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [fetchCases]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 60000); // Refresh every minute
    return () => clearInterval(interval);
  }, [fetchData]);

  // Update URL when tab changes
  const handleTabChange = (tab: TabId) => {
    setActiveTab(tab);
    setSearchParams({ tab });
    setCurrentPage(1); // Reset to first page on tab change
  };

  // Handle page change
  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      fetchCases(newPage);
      // Scroll to top of list
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Handle refresh
  const handleRefresh = () => {
    setRefreshing(true);
    fetchData(currentPage);
  };

  // Navigate to appropriate destination based on what action is needed
  const handleItemClick = (item: WorkItem) => {
    if (item.type === 'case') {
      // Cases always go to the wizard which is the main case management interface
      // If the case has transactions needing review, go directly to transactions section
      let url = item.primary_action_url || `/wizard-v2?claim=${item.id}`;
      if (item.transactions_need_review && item.transactions_need_review > 0) {
        // Add section parameter to go directly to transactions view
        url = `/wizard-v2?claim=${item.id}&section=transactions`;
      }
      navigate(url);
    } else if (item.type === 'transaction' || item.type === 'awaiting_response') {
      // For client responses, go to the clarification review page
      navigate(`/cleanup-validation?claim=${item.report_id}`);
    } else if (item.type === 'issue') {
      // For issues, navigate to the case wizard with issue context
      navigate(`/wizard-v2?claim=${item.report_id}&section=issues&issue=${item.id}`);
    }
  };

  // Approve transaction
  const handleApproveTransaction = async (item: TransactionItem, e: React.MouseEvent) => {
    e.stopPropagation();
    setProcessingId(item.id);
    try {
      await api.post(`/api/v1/clarifications/responses/${item.id}/review?approved=true&apply_to_draft=true`, null, { showErrorToast: false });
      toast.success(`Transaction approved for ${item.claimant_name}`);
      // Remove from list
      setTransactions(prev => prev.filter(t => t.id !== item.id));
    } catch (err) {
      toast.error('Failed to approve transaction');
    } finally {
      setProcessingId(null);
    }
  };

  // Get filtered items based on active tab and search
  const getFilteredItems = (): WorkItem[] => {
    let items: WorkItem[] = [];
    
    switch (activeTab) {
      case 'needs_review':
        // Cases that have transactions needing review AND are NOT awaiting client
        items = cases.filter(c => c.transactions_need_review > 0 && c.workflow_status !== 'awaiting_client');
        break;
      case 'awaiting':
        // Cases waiting on client - either from workflow_status OR from clarifications
        // Include cases with workflow_status: "awaiting_client"
        const awaitingCases = cases.filter(c => c.workflow_status === 'awaiting_client');
        // Combine with awaitingResponse (clarification requests)
        items = [...awaitingCases, ...awaitingResponse];
        break;
      case 'issues':
        items = issues;
        break;
    }

    // Apply search filter - searches ALL cases, not just filtered tab
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      // When searching, search across ALL cases regardless of tab
      const searchableItems: WorkItem[] = [...cases, ...awaitingResponse, ...issues];
      items = searchableItems.filter(item => {
        if (item.type === 'case') {
          return item.business_name?.toLowerCase().includes(query) ||
                 item.law_firm?.toLowerCase().includes(query);
        } else if (item.type === 'transaction' || item.type === 'awaiting_response') {
          return item.claimant_name?.toLowerCase().includes(query) ||
                 item.law_firm_name?.toLowerCase().includes(query) ||
                 item.transaction_description?.toLowerCase().includes(query);
        } else if (item.type === 'issue') {
          return item.title?.toLowerCase().includes(query) ||
                 item.description?.toLowerCase().includes(query);
        }
        return false;
      });
    }

    return sortByPriorityAndAge(items);
  };

  const filteredItems = getFilteredItems();

  // ==================== Render Functions ====================

  const renderWorkItem = (item: WorkItem, index: number) => {
    // Get display values based on item type
    let title = '';
    let subtitle = '';
    let actionText = '';

    if (item.type === 'case') {
      title = item.business_name || 'Unknown Case';
      subtitle = item.law_firm || 'No law firm';
      actionText = item.transactions_need_review > 0 
        ? `${item.transactions_need_review} transactions need review`
        : item.queue_reason || 'Data entry in progress';
    } else if (item.type === 'transaction' || item.type === 'awaiting_response') {
      // For clarification items, show question as title if no claimant name
      title = item.claimant_name || item.question?.slice(0, 40) || 'Client Response Needed';
      subtitle = item.law_firm_name || 'Unassigned Firm';
      actionText = item.client_response 
        ? `Response: "${item.client_response.slice(0, 50)}${item.client_response.length > 50 ? '...' : ''}"`
        : item.question?.slice(0, 60) || 'Awaiting client response';
    } else if (item.type === 'issue') {
      title = item.title;
      subtitle = `Case: ${item.report_id.slice(0, 8)}...`;
      // Prefer the actionable remediation (what_would_fix) over the technical
      // description for the "Action Needed" column; fall back to description.
      const actionSrc = item.what_would_fix || item.description || '';
      actionText = actionSrc.slice(0, 80) + (actionSrc.length > 80 ? '...' : '');
    }

    // Determine status for the colored dot
    // Status logic: stale > review > processing > waiting > ready
    let status: 'stale' | 'review' | 'processing' | 'waiting' | 'ready' = 'ready';
    if (item.type === 'case') {
      if (item.days_stale >= 7) {
        status = 'stale';
      } else if (item.transactions_need_review > 0) {
        status = 'review';
      } else if (item.pipeline_stage === 'ai_processing' || item.assigned_queue === 'ai_processing') {
        status = 'processing';
      } else if (item.assigned_queue === 'client') {
        status = 'waiting';
      } else {
        status = 'ready';
      }
    }

    // Status dot colors — orange = needs-action/attention, green = done, neutral otherwise
    const statusDotColors: Record<string, string> = {
      stale: 'var(--color-brand)',     // Orange for stale (attention)
      review: 'var(--color-brand)',    // Orange for needs review (attention)
      processing: 'var(--color-brand)',// Orange for processing (active)
      waiting: 'var(--color-status-queued)',   // Neutral for waiting
      ready: 'var(--color-verified)',       // Green for ready (done)
    };

    // Get completion percentage for progress bar
    const completionPct = item.type === 'case' ? (item.completion_percentage || 0) : 0;

    // Progress bar color based on completion — green only when complete, else orange (in progress)
    const getProgressColor = (pct: number) => {
      if (pct >= 80) return 'var(--color-verified)';
      if (pct >= 50) return 'var(--color-brand)';
      if (pct >= 25) return 'var(--color-brand)';
      return 'var(--color-brand)';
    };

    return (
      <div
        key={`${item.type}-${item.id}-${index}`}
        onClick={() => handleItemClick(item)}
        className="grid grid-cols-12 gap-4 px-5 py-3 cursor-pointer transition-colors items-center"
        style={{ borderBottom: '1px solid var(--color-border-faint)' }}
        onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-bg-base)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
      >
        {/* Status Dot */}
        <div className="col-span-1 flex justify-center">
          <span
            className="inline-block w-2.5 h-2.5 rounded-full"
            style={{ background: statusDotColors[status] }}
            title={status}
          />
        </div>

        {/* Case Name + Law Firm stacked */}
        <div className="col-span-4 min-w-0">
          <div className="cs-body truncate" style={{ color: 'var(--color-text-primary)', fontWeight: 500 }}>{title || 'No Title'}</div>
          <div className="cs-meta truncate">{subtitle || 'No law firm'}</div>
        </div>

        {/* Action/Description */}
        <div className="col-span-4 min-w-0">
          <span className="cs-body truncate block">{actionText || 'No action'}</span>
        </div>

        {/* Progress Bar */}
        <div className="col-span-3 flex items-center gap-2">
          <div className="flex-1 h-2 rounded-full overflow-hidden" style={{ background: 'var(--color-bg-elevated)' }}>
            <div
              className="h-full transition-all duration-300"
              style={{ width: `${completionPct}%`, background: getProgressColor(completionPct) }}
            />
          </div>
          <span className="cs-figure w-10 text-right" style={{ color: 'var(--color-text-muted)' }}>{completionPct}%</span>
          <ChevronRight className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--color-text-dim)' }} />
        </div>
      </div>
    );
  };

  // ==================== Main Render ====================

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="w-8 h-8 animate-spin" style={{ color: 'var(--color-brand)' }} />
          <span className="cs-body ml-3">Loading work queue...</span>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <Toaster position="top-right" richColors />
      <div className="p-6" style={{ background: 'var(--color-bg-base)' }}>
        {/* Header */}
        <div className="flex items-center justify-between mb-6 max-w-7xl mx-auto">
          <div>
            <h1 className="cs-display flex items-center gap-3">
              <Inbox className="w-7 h-7" style={{ color: 'var(--color-brand)' }} />
              Work Queue
            </h1>
            <p className="cs-meta mt-1">
              All your work items in one place
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowSidebar(!showSidebar)}
              className="cs-body px-3 py-2 rounded-lg flex items-center gap-2"
              style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)', color: 'var(--color-text-primary)' }}
              title={showSidebar ? 'Hide sidebar' : 'Show sidebar'}
            >
              {showSidebar ? <PanelRightClose className="w-4 h-4" /> : <PanelRightOpen className="w-4 h-4" />}
            </button>
            <Link
              to="/dashboard"
              className="cs-body"
              style={{ color: 'var(--color-needs-human-text)' }}
            >
              ← Back to Dashboard
            </Link>
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="cs-body px-4 py-2 rounded-lg flex items-center gap-2 disabled:opacity-50"
              style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)', color: 'var(--color-text-primary)' }}
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>

        {/* Main Content with Sidebar */}
        <div className={`flex gap-6 ${showSidebar ? '' : 'max-w-7xl mx-auto'}`}>
          {/* Main Queue Section */}
          <div className={`flex-1 ${showSidebar ? 'max-w-5xl' : ''}`}>
            {/* Stats Row */}
            {stats && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <StatCard
                  label="All Cases"
                  value={stats.needs_attention}
                  icon={Inbox}
                  color="var(--color-text-primary)"
                  bgColor="var(--color-bg-surface)"
                />
                <StatCard
                  label="Needs Review"
                  value={stats.cases_with_transactions_needing_review || 0}
                  icon={UserCheck}
                  color="var(--color-needs-human-text)"
                  bgColor="var(--color-bg-surface)"
                />
                <StatCard
                  label="Awaiting Client"
                  value={cases.filter(c => c.workflow_status === 'awaiting_client').length + awaitingResponse.length}
                  icon={Clock}
                  color="var(--color-text-primary)"
                  bgColor="var(--color-bg-surface)"
                />
                <StatCard
                  label="Stale (7+ days)"
                  value={(stats.stale_7_days || 0) + (stats.stale_14_days || 0)}
                  icon={AlertTriangle}
                  color="var(--color-needs-human-text)"
                  bgColor="var(--color-bg-surface)"
                />
              </div>
            )}

        {/* Tabs */}
        <div className="overflow-hidden" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)', borderRadius: 14 }}>
          {/* Tab Bar */}
          <div className="flex items-center" style={{ borderBottom: '1px solid var(--color-border-sub)', background: 'var(--color-bg-base)' }}>
            {TABS.map((tab) => {
              const Icon = tab.icon;
              // Calculate count based on tab type
              let count = 0;
              switch (tab.id) {
                case 'needs_review':
                  // Cases needing review that are NOT awaiting client
                  count = cases.filter(c => c.transactions_need_review > 0 && c.workflow_status !== 'awaiting_client').length;
                  break;
                case 'awaiting':
                  // Cases with workflow_status: awaiting_client + clarification requests
                  count = cases.filter(c => c.workflow_status === 'awaiting_client').length + awaitingResponse.length;
                  break;
                case 'issues':
                  count = issues.length;
                  break;
              }
              
              return (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className="cs-body flex items-center gap-2 px-5 py-3 transition-colors -mb-px"
                  style={{
                    borderBottom: '2px solid',
                    borderBottomColor: activeTab === tab.id ? 'var(--color-brand)' : 'transparent',
                    color: activeTab === tab.id ? 'var(--color-needs-human-text)' : 'var(--color-text-muted)',
                    background: activeTab === tab.id ? 'var(--color-bg-surface)' : 'transparent',
                    fontWeight: 500,
                  }}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                  <span
                    className="cs-chip px-2 py-0.5 rounded-full"
                    style={
                      activeTab === tab.id
                        ? { background: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }
                        : { background: 'var(--color-bg-elevated)', color: 'var(--color-text-muted)' }
                    }
                  >
                    {count}
                  </span>
                </button>
              );
            })}

            {/* Search on the right */}
            <div className="ml-auto px-4 py-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--color-text-dim)' }} />
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="cs-body pl-9 pr-4 py-1.5 w-64 rounded-lg"
                  style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', color: 'var(--color-text-primary)' }}
                />
              </div>
            </div>
          </div>

          {/* Table Header */}
          <div className="cs-label grid grid-cols-12 gap-4 px-5 py-3" style={{ background: 'var(--color-bg-base)', borderBottom: '1px solid var(--color-border-sub)' }}>
            <div className="col-span-1 text-center">Status</div>
            <div className="col-span-4">Case</div>
            <div className="col-span-4">Action Needed</div>
            <div className="col-span-3">Progress</div>
          </div>

          {/* Content */}
          {filteredItems.length === 0 ? (
            <div className="p-12 text-center">
              <CheckCircle2 className="w-12 h-12 mx-auto mb-3" style={{ color: 'var(--color-verified)' }} />
              <h3 className="cs-h3 mb-1">All Caught Up!</h3>
              <p className="cs-meta">
                {searchQuery
                  ? 'No items match your search.'
                  : 'No items currently need your attention.'
                }
              </p>
            </div>
          ) : (
            <div className="max-h-[600px] overflow-y-auto">
              {filteredItems.map(renderWorkItem)}
            </div>
          )}
          
          {/* Pagination - only show if filtered items exceed page size */}
          {filteredItems.length > pageSize && !searchQuery && (
            <div className="flex items-center justify-between px-5 py-4" style={{ borderTop: '1px solid var(--color-border-sub)', background: 'var(--color-bg-base)' }}>
              <div className="cs-meta">
                Showing {pluralize(filteredItems.length, 'item')}
              </div>
            </div>
          )}
        </div>
          </div>

          {/* Phase 3: Right Sidebar with Dashboard Widgets */}
          {showSidebar && (
            <div className="w-80 flex-shrink-0 space-y-6">
              {/* Top Priorities */}
              <TopPrioritiesCard maxItems={3} />
              
              {/* Data Quality Overview */}
              <DataQualityWidget compact />
              
              {/* Alerts Panel */}
              <AlertsPanel maxAlerts={5} compact showFilter={false} />
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}

// ==================== Sub-Components ====================

function StatCard({ 
  label, 
  value, 
  icon: Icon, 
  color, 
  bgColor,
  highlight = false,
  isString = false
}: { 
  label: string; 
  value: number | string; 
  icon: React.ElementType; 
  color: string; 
  bgColor: string;
  highlight?: boolean;
  isString?: boolean;
}) {
  return (
    <div
      className="p-4"
      style={{
        background: bgColor,
        border: highlight ? '2px solid var(--color-needs-human-tint-bd)' : '1px solid var(--color-border)',
        borderRadius: 14,
      }}
    >
      <div className="flex items-center gap-2 mb-1">
        <Icon className="w-4 h-4" style={{ color }} />
        <span className="cs-label">{label}</span>
      </div>
      <div className={isString ? 'cs-h2' : 'cs-figure-lg'} style={{ color }}>{value}</div>
    </div>
  );
}
